import { useMemo } from 'react';
import { Space } from 'antd';
import { DragOutlined } from '@ant-design/icons';
import { FormikProps } from 'formik';
import { get } from 'lodash';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { ICS2CountryOfRouting } from 'store/declarations/eu/ics2/ics2-ens-declaration';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import DeclarationSelect from 'components/ui/composed/declarations/formSelect';
import DraggableList from 'components/ui/composed/draggableList/DraggableList';
import { DragHandle } from 'components/ui/composed/draggableList/styles';
import useICS2Codelists from 'hooks/useICS2Codelists';
import useTemplatePath from 'components/ui/composed/template/useTemplatePath';

type Props = {
    formik: FormikProps<any>;
    path: string;
    label: string;
    refNumber: string;
    codelistName: typeof ICS2CodelistsMap[keyof typeof ICS2CodelistsMap];
    getPopupContainer?: () => HTMLElement;
};

/**
 * A routing, of the consignment or of the means of transport. It is picked and ordered as a list of
 * country codes, but the message carries a sequenced entry per country, so the two are mapped onto
 * each other here: the sequence numbers follow the order of the list, which is what the reordering
 * below is for.
 */
const CountriesOfRoutingField = ({ formik, path, label, refNumber, codelistName, getPopupContainer }: Props) => {
    const { getICS2CodeDescription } = useICS2Codelists();

    /**
     * The list is read and written here rather than by the select, because the stored entries and
     * the selected codes are different shapes. In the template builder that means rebasing onto
     * the slot being edited — the select is told to keep the adapter below and leaves the path to
     * us (`keepFieldPropsInTemplate`).
     */
    const scoped = useTemplatePath();
    const valuePath = scoped(path);

    const routing: ICS2CountryOfRouting[] = useMemo(
        () => get(formik.values, valuePath) ?? [],
        [formik.values, valuePath]
    );
    const countries = useMemo(() => routing.map(({ country }) => country).filter(Boolean) as string[], [routing]);

    const setCountries = (updated: string[]) =>
        formik.setFieldValue(
            valuePath,
            updated.map((country, index) => ({ sequenceNumber: index + 1, country }))
        );

    return (
        <>
            <DeclarationSelect
                condensed
                mode="multiple"
                hideCodelistMenu
                hideKeys
                required
                label={label}
                refNumber={refNumber}
                codeValidation={[]}
                codelistProps={{ codelistName, declarationType: DeclarationTypes.ICS2 }}
                getPopupContainer={getPopupContainer}
                keepFieldPropsInTemplate
                fieldProps={{
                    // `name` stays unscoped: the select keys its meta and view/edit controls off
                    // it, and rebases it itself. Only the value adapter below is ours.
                    ...formik.getFieldProps(path),
                    value: countries,
                    onChange: ({ target }: any) => setCountries(target?.value ?? []),
                }}
                fieldMeta={formik.getFieldMeta(valuePath)}
                fieldHelper={formik.getFieldHelpers(valuePath)}
            />
            <DraggableList
                items={countries}
                onReorder={setCountries}
                getKey={(countryCode, index) => `${countryCode}-${index}`}
                renderItem={(countryCode, index) => (
                    <Space size={8}>
                        <DragHandle>
                            <DragOutlined style={{ color: 'var(--brand-6)' }} />
                        </DragHandle>
                        <span>Step {index + 1}:</span>
                        <strong>{getICS2CodeDescription(countryCode, codelistName, true)}</strong>
                    </Space>
                )}
            />
        </>
    );
};

export default CountriesOfRoutingField;
